import { ConfigStateService, TimeService, TimezoneService } from '@abp/ng.core';
import { Injectable, inject } from '@angular/core';
import { Observable, distinctUntilChanged, map } from 'rxjs';
import { formatPhoneNumber } from 'src/core/helpers/phone-number-format.helper';
import { DateDisplayFormat } from 'src/core/models/settings/localization/date-display-format.enum';
import { LOCALIZATION_SETTING_KEYS } from 'src/core/models/settings/localization/localization-setting-keys.constant';
import { NumberDisplayFormat } from 'src/core/models/settings/localization/number-display-format.enum';
import { PhoneNumberDisplayFormat } from 'src/core/models/settings/localization/phone-number-display-format.enum';
import { TimeDisplayFormat } from 'src/core/models/settings/localization/time-display-format.enum';

/** Grouping and decimal separators implied by a `NumberDisplayFormat`. */
export interface NumberSeparators {
  group: string;
  decimal: string;
}

/** Luxon patterns per `DateDisplayFormat`, matching the labels shown on the settings screen. */
const DATE_PATTERNS: Record<DateDisplayFormat, string> = {
  [DateDisplayFormat.DayMonthYear]: 'dd/MM/yyyy',
  [DateDisplayFormat.MonthDayYear]: 'MM/dd/yyyy',
  [DateDisplayFormat.YearMonthDay]: 'yyyy-MM-dd',
};

/** Luxon patterns per `TimeDisplayFormat`. */
const TIME_PATTERNS: Record<TimeDisplayFormat, string> = {
  [TimeDisplayFormat.TwentyFourHour]: 'HH:mm',
  [TimeDisplayFormat.TwelveHour]: 'hh:mm a',
};

/** Luxon patterns per `TimeDisplayFormat` for call sites that need second precision. */
const TIME_PATTERNS_WITH_SECONDS: Record<TimeDisplayFormat, string> = {
  [TimeDisplayFormat.TwentyFourHour]: 'HH:mm:ss',
  [TimeDisplayFormat.TwelveHour]: 'hh:mm:ss a',
};

/** Separators per `NumberDisplayFormat`, matching the `1.234,56` / `1,234.56` sample labels. */
const NUMBER_SEPARATORS: Record<NumberDisplayFormat, NumberSeparators> = {
  [NumberDisplayFormat.DotThousandCommaDecimal]: { group: '.', decimal: ',' },
  [NumberDisplayFormat.CommaThousandDotDecimal]: { group: ',', decimal: '.' },
};

/** Enum member name (as persisted by the backend) to enum value. */
const DATE_FORMAT_BY_NAME: Record<string, DateDisplayFormat> = {
  daymonthyear: DateDisplayFormat.DayMonthYear,
  monthdayyear: DateDisplayFormat.MonthDayYear,
  yearmonthday: DateDisplayFormat.YearMonthDay,
};

const TIME_FORMAT_BY_NAME: Record<string, TimeDisplayFormat> = {
  twentyfourhour: TimeDisplayFormat.TwentyFourHour,
  twelvehour: TimeDisplayFormat.TwelveHour,
};

const PHONE_FORMAT_BY_NAME: Record<string, PhoneNumberDisplayFormat> = {
  international: PhoneNumberDisplayFormat.International,
  local: PhoneNumberDisplayFormat.Local,
  digitsonly: PhoneNumberDisplayFormat.DigitsOnly,
};

const NUMBER_FORMAT_BY_NAME: Record<string, NumberDisplayFormat> = {
  dotthousandcommadecimal: NumberDisplayFormat.DotThousandCommaDecimal,
  commathousanddotdecimal: NumberDisplayFormat.CommaThousandDotDecimal,
};

/** Mirrors `LocalizationSettingsProvider.DefaultLanguage`. */
const DEFAULT_LANGUAGE = 'tr';

/**
 * Synchronous, typed access to the tenant scoped display preferences of
 * Administration &gt; Settings &gt; Localization.
 *
 * The values travel with the ABP application configuration, so no HTTP call is needed here — this
 * is the read path used by the `display*` pipes. `LocalizationSettingsService` remains the write
 * path used by the settings screen; it refreshes the application state after a save.
 *
 * The time zone preference is deliberately out of scope: date rendering keeps using the zone the
 * rest of the application already renders in.
 */
@Injectable({ providedIn: 'root' })
export class LocalizationDisplayService {
  private readonly config = inject(ConfigStateService);
  private readonly timeService = inject(TimeService);
  private readonly timezoneService = inject(TimezoneService);

  /** Tenant date pattern. Falls back to the backend default when unset or unrecognised. */
  get dateFormat(): DateDisplayFormat {
    return this.parse(
      LOCALIZATION_SETTING_KEYS.dateFormat,
      DATE_FORMAT_BY_NAME,
      DateDisplayFormat.DayMonthYear
    );
  }

  /** Tenant clock pattern. Falls back to the backend default when unset or unrecognised. */
  get timeFormat(): TimeDisplayFormat {
    return this.parse(
      LOCALIZATION_SETTING_KEYS.timeFormat,
      TIME_FORMAT_BY_NAME,
      TimeDisplayFormat.TwentyFourHour
    );
  }

  /** Tenant phone number pattern. Falls back to the backend default when unset or unrecognised. */
  get phoneNumberFormat(): PhoneNumberDisplayFormat {
    return this.parse(
      LOCALIZATION_SETTING_KEYS.phoneNumberFormat,
      PHONE_FORMAT_BY_NAME,
      PhoneNumberDisplayFormat.International
    );
  }

  /** Tenant grouping/decimal pattern. Falls back to the backend default when unset or unrecognised. */
  get numberFormat(): NumberDisplayFormat {
    return this.parse(
      LOCALIZATION_SETTING_KEYS.numberFormat,
      NUMBER_FORMAT_BY_NAME,
      NumberDisplayFormat.DotThousandCommaDecimal
    );
  }

  /**
   * Tenant culture name, e.g. `tr`.
   *
   * Prefers the stored setting, then the culture the application configuration actually resolved
   * for this request, and only then the backend default — so the value stays right even if the
   * setting is not exposed to clients.
   */
  get language(): string {
    const setting = this.config.getSetting(LOCALIZATION_SETTING_KEYS.language);
    if (setting && setting.trim() !== '') {
      return setting.trim();
    }

    const currentCulture = this.config.getDeep('localization.currentCulture.cultureName') as
      | string
      | undefined;

    return currentCulture && currentCulture.trim() !== ''
      ? currentCulture.trim()
      : DEFAULT_LANGUAGE;
  }

  /** Luxon date pattern for the tenant date format, e.g. `dd/MM/yyyy`. */
  get datePattern(): string {
    return DATE_PATTERNS[this.dateFormat];
  }

  /** Luxon time pattern for the tenant time format, e.g. `HH:mm`. */
  get timePattern(): string {
    return TIME_PATTERNS[this.timeFormat];
  }

  /** Luxon time pattern for the tenant time format including seconds, e.g. `HH:mm:ss`. */
  get timeWithSecondsPattern(): string {
    return TIME_PATTERNS_WITH_SECONDS[this.timeFormat];
  }

  /** Luxon pattern combining the tenant date and time formats. */
  get dateTimePattern(): string {
    return `${this.datePattern} ${this.timePattern}`;
  }

  /** Grouping and decimal separators for the tenant number format. */
  get numberSeparators(): NumberSeparators {
    return NUMBER_SEPARATORS[this.numberFormat];
  }

  /**
   * A hash of the current tenant display settings. Used by impure pipes to skip recalculation
   * when neither the input nor the settings have changed.
   */
  get settingsHash(): string {
    return `${this.dateFormat}|${this.timeFormat}|${this.numberFormat}|${this.phoneNumberFormat}|${this.language}`;
  }

  /**
   * Display name of a culture as advertised by the application configuration, e.g. `tr` →
   * `Türkçe`. Returns `null` when the culture is not among the configured languages.
   */
  getLanguageDisplayName(cultureName: string): string | null {
    const normalized = cultureName?.trim().toLowerCase();
    if (!normalized) {
      return null;
    }

    const languages = this.config.getDeep('localization.languages') as
      | { cultureName?: string; displayName?: string }[]
      | undefined;

    const match = languages?.find(
      language => language.cultureName?.toLowerCase() === normalized
    );

    return match?.displayName ?? null;
  }

  /**
   * Renders the date part of a timestamp with the tenant date format, e.g. `06/08/2026`.
   * Returns `null` for empty input and for values that are not a valid date.
   *
   * `timezone` is a passthrough for call sites that already pin a zone — a birth date stored as a
   * plain UTC midnight, for instance, must not drift a day. It is not the tenant time zone
   * preference; omitting it renders in the zone the rest of the application uses.
   */
  formatDate(
    value: Date | string | number | null | undefined,
    timezone?: string
  ): string | null {
    return this.format(value, this.datePattern, timezone);
  }

  /** Renders the clock part of a timestamp with the tenant time format, e.g. `14:35`. */
  formatTime(
    value: Date | string | number | null | undefined,
    withSeconds = false,
    timezone?: string
  ): string | null {
    return this.format(
      value,
      withSeconds ? this.timeWithSecondsPattern : this.timePattern,
      timezone
    );
  }

  /** Renders a timestamp with the tenant date and time formats, e.g. `06/08/2026 14:35`. */
  formatDateTime(
    value: Date | string | number | null | undefined,
    withSeconds = false,
    timezone?: string
  ): string | null {
    const timePattern = withSeconds ? this.timeWithSecondsPattern : this.timePattern;

    return this.format(value, `${this.datePattern} ${timePattern}`, timezone);
  }

  /**
   * Renders a number with the tenant grouping and decimal separators.
   *
   * `digitsInfo` follows the Angular `DecimalPipe` syntax (`minIntegerDigits.minFraction-maxFraction`)
   * and defaults to `1.0-2`. Returns `'-'` for empty input and echoes non numeric input unchanged.
   */
  formatNumber(value: number | string | null | undefined, digitsInfo?: string): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const numericValue = typeof value === 'string' ? Number(value) : value;
    if (!Number.isFinite(numericValue)) {
      return String(value);
    }

    const separators = this.numberSeparators;

    // A fixed locale gives predictable grouping; the separators themselves come from the tenant
    // setting, which is independent of the chosen language.
    return new Intl.NumberFormat('en-US', this.toNumberFormatOptions(digitsInfo))
      .formatToParts(numericValue)
      .map(part => {
        if (part.type === 'group') {
          return separators.group;
        }

        return part.type === 'decimal' ? separators.decimal : part.value;
      })
      .join('');
  }

  /** Renders a phone number with the tenant phone number format. */
  formatPhoneNumber(value: string | null | undefined): string {
    if (value === null || value === undefined || value.trim() === '') {
      return '-';
    }

    return formatPhoneNumber(value, this.phoneNumberFormat);
  }

  /** Emits whenever the tenant display preferences change, e.g. after the settings screen saves. */
  get changes$(): Observable<string> {
    return this.config
      .getSettings$('Localization.')
      .pipe(
        map(settings => `${JSON.stringify(settings)}|${this.language}`),
        distinctUntilChanged()
      );
  }

  /**
   * Shared date rendering. Defaults to the zone the rest of the application already renders in —
   * the tenant time zone preference is intentionally not consumed here.
   */
  private format(
    value: Date | string | number | null | undefined,
    pattern: string,
    timezone?: string
  ): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
      return null;
    }

    return this.timeService.format(date, pattern, this.normalizeZone(timezone));
  }

  /** Accepts the `+0000` spelling used by existing call sites alongside IANA zone names. */
  private normalizeZone(timezone?: string): string {
    if (!timezone) {
      return this.timezoneService.timezone;
    }

    return timezone === '+0000' ? 'UTC' : timezone;
  }

  /** Translates Angular's `DecimalPipe` digits syntax into `Intl.NumberFormat` options. */
  private toNumberFormatOptions(digitsInfo?: string): Intl.NumberFormatOptions {
    const options: Intl.NumberFormatOptions = {
      minimumIntegerDigits: 1,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    };

    if (!digitsInfo) {
      return options;
    }

    const [integerPart, fractionPart] = digitsInfo.split('.');

    const minimumIntegerDigits = parseInt(integerPart, 10);
    if (Number.isFinite(minimumIntegerDigits) && minimumIntegerDigits > 0) {
      options.minimumIntegerDigits = minimumIntegerDigits;
    }

    if (!fractionPart) {
      return options;
    }

    const [minimum, maximum] = fractionPart.split('-');

    const minimumFractionDigits = parseInt(minimum, 10);
    if (Number.isFinite(minimumFractionDigits)) {
      options.minimumFractionDigits = minimumFractionDigits;
    }

    const maximumFractionDigits = parseInt(maximum, 10);
    options.maximumFractionDigits = Number.isFinite(maximumFractionDigits)
      ? maximumFractionDigits
      : options.minimumFractionDigits;

    return options;
  }

  private parse<TEnum>(key: string, byName: Record<string, TEnum>, fallback: TEnum): TEnum {
    const raw = this.config.getSetting(key);
    if (!raw) {
      return fallback;
    }

    return byName[raw.trim().toLowerCase()] ?? fallback;
  }
}
